import { useSubscription } from "@apollo/client";
import gql from "graphql-tag";
import * as Cookies from "js-cookie";
import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { toastErrorOptions } from "../../../../App";
import { noop } from "../../../../utils/noop";
import { Icon } from "../../../Icon/Icon";

/**
 * ApplicationDisplayItem
 *
 * @param {object} props
 * @prop {string} props.accountId
 * @prop {boolean} props.canManage
 * @prop {string} props.distributorId
 * @prop {object} props.app
 * @prop {string} props.app.id
 * @prop {string} props.app.name
 * @prop {boolean} props.app.supportAccess
 * @prop {string} props.app.type
 *
 * @return {*}
 */
export const ApplicationDisplayItem = ({
  app,
  accountId,
  canManage,
  distributorId,
}) => {
  const accessLevel = Cookies.get("access_level");

  const { t } = useTranslation();
  useSubscription(appUpdatedSubscription, {
    variables: { id: app.id },
    onSubscriptionData: ({ client: { cache }, subscriptionData }) => {
      try {
        const prev = cache.readQuery({
          query: getAppsQuery,
          variables: { accountIds: [accountId] },
        });

        const newAppData = subscriptionData?.data?.onAppAccountUpdated;

        const updatedData = prev.apps.map((a) => {
          if (a.id === newAppData.id) {
            return newAppData;
          } else {
            return a;
          }
        });

        cache.writeQuery({
          query: getAppsQuery,
          data: updatedData,
          variables: { accountId },
        });
      } catch (err) {
        noop();
      }
    },
  });
  const isSupportable = app.supportAccess;
  const supportStyles = ["ApplicationDisplayItem__support"];
  if (!isSupportable) {
    supportStyles.push("ApplicationDisplayItem__support--disabled");
  }

  const giveSupportHandler = async () => {
    try {
      loginToApp(app.id, app.type);
    } catch (err) {
      toast("An error occured", toastErrorOptions);
    }
  };

  const loginToApp = (appId, appType) => {
    const baseUrl = getAppUrl(appType);
    window.open(`${baseUrl}/?account_id=${appId}`);
  };

  const getIndicatorColor = () => {
    switch (app.type) {
      case "INVENTORY":
        return "#F4BD48";

      case "WORKFLOW":
        return "#008AC6";

      default:
        return "#008AC6";
    }
  };

  const getAppUrl = (app) => {
    switch (app) {
      case "WORKFLOW":
        return process.env.REACT_APP_WORKFLOW_URL;

      case "INVENTORY":
        return process.env.REACT_APP_INVENTORY_URL;

      default:
        return "";
    }
  };

  const handleGoToUrl = () => {
    const appUrl = getAppUrl(app.type);
    if (
      (accessLevel === "SUPERADMIN" || accessLevel === "DISTRIBUTOR") &&
      canManage
    ) {
      window.open(
        appUrl +
          `/?distributor_id=${distributorId}&app_id=${app.id}&access_level=${accessLevel}`
      );
    }
  };

  return (
    <div className="ApplicationDisplayItem">
      <div
        className="ApplicationDisplayItem__indicator"
        style={{ backgroundColor: getIndicatorColor() }}
      ></div>
      <div className="ApplicationDisplayItem__icon">
        <Icon name="menuAccounts" width={14} height={20} />
      </div>
      <div className="ApplicationDisplayItem__title">{app.name}</div>
      <div className="ApplicationDisplayItem__actions">
        {canManage && (
          <div
            className="ApplicationDisplayItem__manage"
            onClick={() => handleGoToUrl()}
          >
            <Icon name="iconExternal" width={12} height={12} />
            <div className="ApplicationDisplayItem__manage--text">
              {t("Manage")}
            </div>
          </div>
        )}
        <div
          className={supportStyles.join(" ")}
          onClick={!isSupportable ? noop : giveSupportHandler}
        >
          {isSupportable ? (
            <Icon name="iconUnlocked" width={13} height={12} />
          ) : (
            <Icon name="iconLock" width={9} height={12} />
          )}
          <div className="ApplicationDisplayItem__support-text">
            {t("Support login")}
          </div>
        </div>
      </div>
    </div>
  );
};

const getAppsQuery = gql`
  query apps($accountIds: [String]) {
    apps(accountIds: $accountIds) {
      id
      name
      type
      supportAccess
    }
  }
`;

const appUpdatedSubscription = gql`
  subscription onAppAccountUpdated($id: String) {
    onAppAccountUpdated(id: $id) {
      id
      name
      type
      supportAccess
    }
  }
`;
